import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Database,
  X,
  XCircle,
} from 'lucide-react';
import {
  api,
  type DatalakeRunServer,
  type DatalakeRunStatus,
  type DatalakeRunSummary,
} from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatBytes, formatDateTime, formatNumber, formatRelative } from '../lib/format';
import { cn } from '../lib/utils';

const STATUS_LABEL: Record<DatalakeRunStatus, string> = {
  success: 'Succeeded',
  partial: 'Partial',
  failed: 'Failed',
  'no-work': 'No work',
  unknown: 'Unknown',
};

const STATUS_VARIANT: Record<
  DatalakeRunStatus,
  'success' | 'warning' | 'destructive' | 'secondary' | 'outline'
> = {
  success: 'success',
  partial: 'warning',
  failed: 'destructive',
  'no-work': 'secondary',
  unknown: 'outline',
};

const getRunValue = (item: DatalakeRunSummary, key: string): unknown => {
  switch (key) {
    case 'runId':
      return item.runId ?? item.lastModified ?? '';
    case 'status':
      return item.status;
    case 'totalDates':
      return item.totalDates ?? 0;
    case 'succeeded':
      return item.succeeded ?? 0;
    case 'failed':
      return item.failed ?? 0;
    case 'rawBytes':
      return item.rawBytes ?? 0;
    case 'parquetBytes':
      return item.parquetBytes ?? 0;
    case 'compressionRatio':
      return parseFloat(item.compressionRatio ?? '0') || 0;
    default:
      return undefined;
  }
};

const getServerValue = (item: DatalakeRunServer, key: string): unknown => {
  switch (key) {
    case 'clientName':
      return item.clientName ?? '';
    case 'datesProcessed':
      return item.datesProcessed ?? 0;
    case 'datesSucceeded':
      return item.datesSucceeded ?? 0;
    case 'datesFailed':
      return item.datesFailed ?? 0;
    case 'datesEmpty':
      return item.datesEmpty ?? 0;
    case 'rawBytes':
      return item.rawBytes ?? 0;
    case 'parquetBytes':
      return item.parquetBytes ?? 0;
    default:
      return undefined;
  }
};

export function DatalakeRuns() {
  const runs = useQuery({
    queryKey: ['datalake', 'runs'],
    queryFn: () => api.listDatalakeRuns(50),
    staleTime: 5 * 60 * 1000,
  });

  const items = useMemo(() => runs.data?.items ?? [], [runs.data]);
  const { sortedItems, sort, toggle } = useSort(items, getRunValue);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Datalake · Runs</h1>
        <p className="text-muted-foreground">
          Aggregated run logs from <code className="font-mono text-xs">s3://x5-datalake-153020272824/runs/</code>.
          Unreachable servers are named on the row; click through for the per-server breakdown and
          date-level errors. For one customer over time, use{' '}
          <Link to="/datalake/customers" className="text-accent hover:underline">
            Customers
          </Link>
          .
        </p>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Recent runs</CardTitle>
          <span className="text-xs text-muted-foreground">
            {runs.isLoading ? 'Loading…' : `${items.length} runs`}
          </span>
        </CardHeader>
        {runs.error && (
          <div className="px-6 pb-3 text-sm text-destructive">{(runs.error as Error).message}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-y border-border">
              <tr>
                <SortableHeader label="Run" sortKey="runId" sort={sort} onToggle={toggle} />
                <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <SortableHeader label="Dates" sortKey="totalDates" sort={sort} onToggle={toggle} />
                <SortableHeader label="OK" sortKey="succeeded" sort={sort} onToggle={toggle} />
                <SortableHeader label="Failed" sortKey="failed" sort={sort} onToggle={toggle} />
                <SortableHeader label="Raw" sortKey="rawBytes" sort={sort} onToggle={toggle} />
                <SortableHeader label="Parquet" sortKey="parquetBytes" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Compression"
                  sortKey="compressionRatio"
                  sort={sort}
                  onToggle={toggle}
                />
              </tr>
            </thead>
            <tbody>
              {runs.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((run) => (
                <tr
                  key={run.s3Key}
                  onClick={() => setSelectedRunId(extractRunKey(run.s3Key))}
                  className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{run.runId ?? '—'}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatRelative(run.lastModified)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[run.status]}>{STATUS_LABEL[run.status]}</Badge>
                    {run.failedServerNames && run.failedServerNames.length > 0 && (
                      <div className="mt-1 text-xs text-destructive">
                        {run.failedServerNames.slice(0, 3).join(', ')}
                        {run.failedServerNames.length > 3 &&
                          ` +${run.failedServerNames.length - 3} more`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{formatNumber(run.totalDates)}</td>
                  <td className="px-4 py-3 tabular-nums text-success">
                    {formatNumber(run.succeeded)}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 tabular-nums',
                      run.failed && run.failed > 0
                        ? 'text-destructive font-medium'
                        : 'text-muted-foreground',
                    )}
                  >
                    {formatNumber(run.failed)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">
                    {formatBytes(run.rawBytes)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">
                    {formatBytes(run.parquetBytes)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">
                    {run.compressionRatio ?? '—'}
                  </td>
                </tr>
              ))}
              {!runs.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">
                    No runs in the run log bucket yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedRunId && (
        <RunDetailDrawer runId={selectedRunId} onClose={() => setSelectedRunId(null)} />
      )}
    </div>
  );
}

function extractRunKey(s3Key: string): string {
  return s3Key.replace(/^runs\//, '').replace(/\.json$/, '');
}

function RunDetailDrawer({ runId, onClose }: { runId: string; onClose: () => void }) {
  const run = useQuery({
    queryKey: ['datalake', 'run', runId],
    queryFn: () => api.getDatalakeRun(runId),
  });

  const body = run.data?.body;
  const servers = body?.servers ?? [];
  const failedServers = body?.discovery?.failedServers ?? [];

  const {
    sortedItems: sortedServers,
    sort: serverSort,
    toggle: toggleServerSort,
  } = useSort(servers, getServerValue, { key: 'clientName', direction: 'desc' });

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{body?.runId ?? runId}</h2>
            <p className="text-xs text-muted-foreground">
              Written {formatDateTime(run.data?.lastModified)}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {run.isLoading && <p className="text-muted-foreground">Loading run log…</p>}
          {run.error && <p className="text-destructive">{(run.error as Error).message}</p>}

          {body && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Tile
                  label="Servers found"
                  value={formatNumber(body.discovery?.totalServersFound)}
                  icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="With work"
                  value={formatNumber(body.discovery?.serversWithWork)}
                  icon={<Database className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="Succeeded"
                  value={formatNumber(body.processing?.succeeded)}
                  icon={<CheckCircle2 className="h-4 w-4 text-success" />}
                />
                <Tile
                  label="Failed"
                  value={formatNumber(body.processing?.failed)}
                  icon={<XCircle className="h-4 w-4 text-destructive" />}
                  emphasize={!!body.processing?.failed && body.processing.failed > 0}
                />
              </div>

              {failedServers.length > 0 && (
                <section>
                  <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    Servers that failed discovery ({failedServers.length})
                  </h3>
                  <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr className="text-left text-muted-foreground">
                            <th className="px-4 py-2 font-medium">Client</th>
                            <th className="px-4 py-2 font-medium">IP</th>
                            <th className="px-4 py-2 font-medium">Reason</th>
                            <th className="px-4 py-2 font-medium">Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failedServers.map((s, i) => (
                            <tr key={i} className="border-b border-border last:border-0">
                              <td className="px-4 py-2 font-medium">{s.clientName ?? '—'}</td>
                              <td className="px-4 py-2 font-mono text-xs">{s.serverIp ?? '—'}</td>
                              <td className="px-4 py-2">
                                <Badge variant="destructive">{s.reason ?? '—'}</Badge>
                              </td>
                              <td className="px-4 py-2 text-muted-foreground text-xs">
                                {s.detail ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </section>
              )}

              <section>
                <h3 className="text-sm font-medium mb-2">Per-server breakdown ({servers.length})</h3>
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <SortableHeader
                            label="Client"
                            sortKey="clientName"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                          <SortableHeader
                            label="Dates"
                            sortKey="datesProcessed"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                          <SortableHeader
                            label="OK"
                            sortKey="datesSucceeded"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                          <SortableHeader
                            label="Failed"
                            sortKey="datesFailed"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                          <SortableHeader
                            label="Empty"
                            sortKey="datesEmpty"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                          <SortableHeader
                            label="Raw"
                            sortKey="rawBytes"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                          <SortableHeader
                            label="Parquet"
                            sortKey="parquetBytes"
                            sort={serverSort}
                            onToggle={toggleServerSort}
                            className="py-2"
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedServers.map((s) => (
                          <tr key={s.clientName} className="border-b border-border last:border-0">
                            <td className="px-4 py-2 font-medium">{s.clientName}</td>
                            <td className="px-4 py-2 tabular-nums">
                              {formatNumber(s.datesProcessed)}
                            </td>
                            <td className="px-4 py-2 tabular-nums text-success">
                              {formatNumber(s.datesSucceeded)}
                            </td>
                            <td
                              className={cn(
                                'px-4 py-2 tabular-nums',
                                s.datesFailed && s.datesFailed > 0
                                  ? 'text-destructive font-medium'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {formatNumber(s.datesFailed)}
                            </td>
                            <td
                              className={cn(
                                'px-4 py-2 tabular-nums',
                                s.datesEmpty && s.datesEmpty > 0
                                  ? 'text-caution'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {formatNumber(s.datesEmpty)}
                            </td>
                            <td className="px-4 py-2 tabular-nums text-muted-foreground">
                              {formatBytes(s.rawBytes)}
                            </td>
                            <td className="px-4 py-2 tabular-nums text-muted-foreground">
                              {formatBytes(s.parquetBytes)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </section>

              {servers.some((s) => (s.errors?.length ?? 0) > 0) && (
                <section>
                  <h3 className="text-sm font-medium mb-2">Date-level errors</h3>
                  <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr className="text-left text-muted-foreground">
                            <th className="px-4 py-2 font-medium">Client</th>
                            <th className="px-4 py-2 font-medium">Date</th>
                            <th className="px-4 py-2 font-medium">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {servers.flatMap((s) =>
                            (s.errors ?? []).map((e, i) => (
                              <tr
                                key={`${s.clientName}-${i}`}
                                className="border-b border-border last:border-0"
                              >
                                <td className="px-4 py-2 font-medium">{s.clientName}</td>
                                <td className="px-4 py-2 font-mono text-xs">{e.date ?? '—'}</td>
                                <td className="px-4 py-2 text-destructive text-xs">
                                  {e.error ?? '—'}
                                </td>
                              </tr>
                            )),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  icon,
  emphasize,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <Card className={cn(emphasize && 'border-destructive/40')}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold tabular-nums', emphasize && 'text-destructive')}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
